import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, m } from 'motion/react';
import { cn } from '@/lib/cn';
import { DialogHeader, type DialogProps } from './Modal';
import { useBodyScrollLock, useDialogA11y } from './useDialog';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Variante mobile: painel desliza de baixo, full-width, cantos superiores arredondados. */
export function BottomSheet({ open, onClose, title, children, className }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(open);
  useDialogA11y(open, onClose, panelRef);

  return createPortal(
    <AnimatePresence>
      {open && (
        <m.div
          key="overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: EASE }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-end bg-black/40"
        >
          <m.div
            key="panel"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            onClick={(e) => e.stopPropagation()}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.28, ease: EASE }}
            className={cn(
              'max-h-[94dvh] w-full max-w-full overflow-x-hidden overflow-y-auto rounded-t-[var(--radius-card)] bg-surface pb-[env(safe-area-inset-bottom)] shadow-[var(--shadow-modal)] focus:outline-none',
              className,
            )}
          >
            <div className="mx-auto mt-2 h-1 w-9 rounded-full bg-border" aria-hidden />
            <DialogHeader title={title} onClose={onClose} />
            <div className="min-w-0 px-4 pb-4 min-[375px]:px-5 min-[375px]:pb-5">{children}</div>
          </m.div>
        </m.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
